import { cnl } from "csi/typings";
import { action, makeObservable, observable } from "mobx";
import { cloneObjectWithProxy } from "csi/utility/mobxUtility";

/**
 * This store contains data persisted to local storage using the mobx-sync library.
 * If an area of the application has multiple properties to store, consider making a
 * class similar to LocalDataSetup.
 */

function removeFromArray(arr: string[], value: string): void {
	const index = arr.indexOf(value);
	if (index >= 0) {
		arr.splice(index, 1);
	}
}

export class LocalFormulatedProduct {
	/** if true, then Flat Ingredients list should be shown */
	@observable lastToggleStates: Map<cnl, boolean> = new Map<cnl, boolean>();

	constructor() {
		makeObservable(this);
	}
}

export class LocalMerchandising {
	/** MerchSeason URL user last visited (on its own, or from a MerchVersion node) */
	@observable seasonUrl: cnl = "";

	constructor() {
		makeObservable(this);
	}
}

export class LocalProductPresentation {
	/** adHoc attribute list user makes */
	adHocIdsList = observable.map<string, string[]>(null, { deep: false });

	/** open state of top drawer in PP filter pane */
	@observable filterTopOpen: boolean;

	/** indicates if attributes should be shown in gallery views */
	@observable showAttributes: boolean;

	/** indicates if attributes should be shown in dialog views */
	@observable showAttributesInDialog: boolean;

	@action setFilterTopOpen = (open: boolean): void => {
		this.filterTopOpen = open;
	};

	@action setAdHocIdsList = (type: string, ids: string[]): void => {
		this.adHocIdsList.set(type, ids);
	};

	constructor() {
		makeObservable(this);
	}
}

export class LocalDataSetup {
	/** list of collapsed groups */
	@observable collapsedGroups: string[] = [];

	/** list of items being viewed as gallery */
	@observable itemsAsGallery: string[] = [];

	/** mapping of section id to item id indicating the last item viewed in the section */
	@observable lastSectionItems: Record<string, string> = {};

	/** indicates if left pane in data setup is open */
	@observable navigationOpen: boolean = true;

	/** indicates if listing sections individually in data setup, as opposed to showing as a select control  */
	@observable sectionSwitcherOpen: boolean = true;

	/**
	 * Add group to set of collapsed groups
	 */
	collapseGroup(groupId: string): void {
		this.collapsedGroups.push(groupId);
	}

	/**
	 * Remove group from set of collapsed groups
	 */
	expandGroup(groupId: string): void {
		removeFromArray(this.collapsedGroups, groupId);
	}

	/**
	 * Return true if group is collapsed
	 */
	isGroupCollapsed(groupId: string): boolean {
		return this.collapsedGroups.includes(groupId);
	}

	/**
	 * Add item to, or remove from, list of items displayed using a gallery view
	 */
	setViewItemAsGallery(itemId: string, asGallery: boolean): void {
		const { itemsAsGallery } = this;

		if (asGallery) {
			itemsAsGallery.push(itemId);
		} else {
			removeFromArray(itemsAsGallery, itemId);
		}
	}

	/**
	 * Return true if given item should be displayed using a gallery view
	 */
	viewItemAsGallery(itemId: string): boolean {
		return this.itemsAsGallery.includes(itemId);
	}

	/** Set open status of section switcher */
	@action setSectionSwitcherOpen = (open: boolean): void => {
		this.sectionSwitcherOpen = open;
	};

	constructor() {
		makeObservable(this);
	}
}

export class LocalRightPane {
	/** width of right pane */
	@observable width: number = 0;

	constructor() {
		makeObservable(this);
	}
}
export default class LocalStore {
	readonly right = new LocalRightPane();
	readonly dataSetup = new LocalDataSetup();
	readonly formulatedProduct = new LocalFormulatedProduct();
	readonly merch = new LocalMerchandising();
	readonly productPresentation = new LocalProductPresentation();

	/** Serialize user ad-hock changes  */
	@observable adhocFilters = {};

	/** Serialize user batchFilters toggle state  */
	@observable batchFiltersOpen: Record<string, boolean> = {};

	/** indicates user has decided to no longer get confirmation dialog when clearing selection during inline edit */
	@observable dismissConfirmationDialogOnClearSelection = false;

	/** locale to display for localized strings and edit in Localization views */
	@observable editLocale = "";

	/** Goto Dialog / Selection Overview Pane drawer open state */
	@observable gotoDialogDrawerOpen: boolean = true;

	/** sort setting to use in image browser */
	@observable imageSort = "";

	/** lightbox info panel minimum width */
	@observable lightboxInfoPanelWidth = 0;

	/** if true, then the last node/page user visited was in admin mode(SiteAdmin page) */
	@observable lastAdminMode = false;
	
	/** Index of selected notification tab */
	@observable notificationTab = 0;

	/** notification unread only */
	@observable notificationsUnreadOnly = false;

	/** history of last view selected per type */
	@observable readonly viewHistory: Record<string, string> = {};

	@action setBatchFiltersOpen = (context: string, state: boolean): void => {
		this.batchFiltersOpen[context] = state;
	};

	constructor() {
		makeObservable(this);
	}

	getAdhocFilters = (context: string):object => {
		return cloneObjectWithProxy(this.adhocFilters[context]);
	};

	@action setAdhocFilters = (context: string, value: object):void => {
		this.adhocFilters[context] = structuredClone(value);
	};
}